import { Component, OnInit, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Visit, VisitStatus } from '../models/visit';
import { ImmobilierService } from '../services/immobilier.service';
import { AddEditVisitDialogComponent } from './add-edit-visit-dialog/add-edit-visit-dialog.component';
import { VisitDetailDialogComponent } from './visit-detail-dialog/visit-detail-dialog.component';
import { ExcelService } from '../excel.service';
import { PdfService } from '../services/pdf.service';


@Component({
  selector: 'app-visites',
  templateUrl: './visites.component.html',
  styleUrls: ['./visites.component.css']
})
export class VisitesComponent implements OnInit {

  displayedColumns: string[] = ['date', 'time', 'property', 'client', 'type', 'agent', 'status', 'actions'];
  dataSource: MatTableDataSource<Visit>;
  visits: Visit[] = [];

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  // Filters
  filterStatus: string = '';
  searchText: string = '';

  // Pagination
  pageSize = 10;

  currentPage = 0;

  // Date Filters
  startDate: Date | null = null;
  endDate: Date | null = null;


  visitStatuses: VisitStatus[] = ['planifiee', 'confirmee', 'terminee', 'annulee', 'reportee', 'en_cours'];

  constructor(
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private immobilierService: ImmobilierService,
    private excelService: ExcelService,
    private pdfService: PdfService
  ) {

    this.dataSource = new MatTableDataSource(this.visits);
  }

  ngOnInit(): void {
    this.loadVisits();
  }

  loadVisits() {
    this.immobilierService.getVisits().subscribe({
      next: (data) => {
        this.visits = data;
        this.dataSource.data = this.visits;
        if (this.paginator) {
          this.dataSource.paginator = this.paginator;
        }
        if (this.sort) {
          this.dataSource.sort = this.sort;
        }
      },
      error: (err) => {
        console.error(err);
        this.showNotification('Erreur lors du chargement des visites');
      }
    });
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;

    // Custom filter predicate
    this.dataSource.filterPredicate = (data: Visit, filter: string) => {
      const searchString = filter.toLowerCase();

      const matchesSearch =
        (data.propertyReference && data.propertyReference.toLowerCase().includes(searchString)) ||
        (data.clientName && data.clientName.toLowerCase().includes(searchString)) ||
        (data.agentName && data.agentName.toLowerCase().includes(searchString));



      const matchesStatus = this.filterStatus ? data.status === this.filterStatus : true;

      // Date Filter
      let matchesDate = true;
      if (this.startDate && this.endDate) {
        const visitDate = new Date(data.visitDate);
        matchesDate = visitDate >= this.startDate && visitDate <= this.endDate;
      } else if (this.startDate) {
        const visitDate = new Date(data.visitDate);
        matchesDate = visitDate >= this.startDate;
      } else if (this.endDate) {
        const visitDate = new Date(data.visitDate);
        matchesDate = visitDate <= this.endDate;
      }

      return matchesSearch && matchesStatus && matchesDate;
    };
  }

  applyFilter() {
    this.dataSource.filter = this.searchText.trim().toLowerCase();
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
    // Force re-filter for dropdown
    this.dataSource.filter = this.searchText.trim().toLowerCase();
  }

  onFilterChange() {
    this.dataSource.filter = this.searchText.trim().toLowerCase() || ' ';
    if (!this.searchText) {
      this.dataSource.filter = ' ';
    }
  }

  onDateChange() {
    this.dataSource.filter = '' + Math.random();
  }

  onPageChange(event: any): void {
    this.pageSize = event.pageSize;
    this.currentPage = event.pageIndex;
  }


  openAddDialog() {
    const dialogRef = this.dialog.open(AddEditVisitDialogComponent, {
      width: '900px',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadVisits();
      }
    });
  }

  openEditDialog(visit: Visit) {
    const dialogRef = this.dialog.open(AddEditVisitDialogComponent, {
      width: '900px',
      disableClose: true,
      data: { visit: visit }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadVisits();
      }
    });
  }

  openDetailDialog(visit: Visit) {
    const dialogRef = this.dialog.open(VisitDetailDialogComponent, {
      width: '800px',
      data: { visit: visit }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        if (result.action === 'edit') {
          this.openEditDialog(result.visit);
        } else if (result.action === 'update') {
          this.immobilierService.updateVisit(result.visit.id, result.visit).subscribe({
            next: () => {
              this.showNotification('Visite mise à jour');
              this.loadVisits();
            },
            error: (err) => {
              console.error(err);
              this.showNotification('Erreur lors de la mise à jour');
            }
          });
        }
      }
    });
  }

  showNotification(message: string) {
    this.snackBar.open(message, 'Fermer', {
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top'
    });
  }

  exportToExcel(): void {
    const dataToExport = this.dataSource.filteredData.map(v => ({
      'Date': v.visitDate,
      'Heure': v.visitTime,
      'Réf. Bien': v.propertyReference,
      'Client': v.clientName,
      'Type': v.type,
      'Agent': v.agentName,
      'Statut': v.status
    }));
    this.excelService.generateExcel(dataToExport, 'Liste_Visites');
  }

  exportToPdf(): void {
    this.pdfService.generatePdf('visites-table-container', 'Liste_Visites');
  }

  printPage(): void {
    window.print();
  }
}

